import type { Router } from '@aikoa/api'
import { useTranslations } from '@aikoa/i18n/react'
import { useMutation, useQuery } from '@tanstack/react-query'
import type { inferRouterOutputs } from '@trpc/server'
import { getDocumentAsync } from 'expo-document-picker'
import { EncodingType, readAsStringAsync } from 'expo-file-system/legacy'
import { useCallback, useMemo, useState } from 'react'
import { Platform, Pressable, ScrollView, View } from 'react-native'
import { useDebouncedCallback } from 'use-debounce'
import { useAlert } from '~/components/GlobalAlert'
import ContentBox from '~/components/layout/ContentBox'
import ScreenView from '~/components/layout/ScreenView'
import { Body, Label } from '~/components/typography/Content'
import { Heading2, Heading3 } from '~/components/typography/Titles'
import { Button, ButtonLabel } from '~/components/ui/button'
import { Input } from '~/components/ui/input'
import { Text } from '~/components/ui/text'
import { trpc } from '~/lib/trpc'

type AppRouterOutputs = inferRouterOutputs<Router>
type CsvImportResult = AppRouterOutputs['admin']['importFeedbackRequestsCsv']
type CsvInvalidContact = CsvImportResult['invalidContacts'][number]
type AdminUser = AppRouterOutputs['admin']['listUsersForAdmin']['users'][number]

function getInvalidContactReasonLabel(
  invalidContactReason: CsvInvalidContact['reason'],
  t: ReturnType<typeof useTranslations<'admin'>>['t']
) {
  switch (invalidContactReason) {
    case 'MISSING_NAME': {
      return t(($) => $.bulkFeedbackImport.invalidReasons.missingName)
    }
    case 'MISSING_CONTACT_INFO': {
      return t(($) => $.bulkFeedbackImport.invalidReasons.missingPhone)
    }
    case 'INVALID_PHONE': {
      return t(($) => $.bulkFeedbackImport.invalidReasons.invalidPhone)
    }
    case 'INVALID_EMAIL': {
      return t(($) => $.bulkFeedbackImport.invalidReasons.invalidEmail)
    }
    case 'INVALID_LANGUAGE': {
      return t(($) => $.bulkFeedbackImport.invalidReasons.invalidLanguage)
    }
    default: {
      return t(($) => $.bulkFeedbackImport.invalidReasons.unknown)
    }
  }
}

function formatInvalidContactLine(invalidContact: CsvInvalidContact): string {
  const values: string[] = []

  if (invalidContact.name) {
    values.push(invalidContact.name)
  }

  if (invalidContact.phone) {
    values.push(invalidContact.phone)
  }

  if (invalidContact.email) {
    values.push(invalidContact.email)
  }

  if (invalidContact.language) {
    values.push(invalidContact.language)
  }

  return values.join(' | ')
}

async function readCsvContent(fileUri: string): Promise<string> {
  if (Platform.OS === 'web') {
    const response = await fetch(fileUri)
    return await response.text()
  }

  return await readAsStringAsync(fileUri, {
    encoding: EncodingType.UTF8,
  })
}

function UserSelectRow({
  user,
  isSelected,
  onSelect,
}: {
  user: AdminUser
  isSelected: boolean
  onSelect: (user: AdminUser) => void
}) {
  const { t } = useTranslations('admin')

  return (
    <Pressable
      onPress={() => onSelect(user)}
      className={`gap-1 rounded-lg border p-3 ${
        isSelected ? 'border-black bg-neutral-100' : 'border-neutral-200 bg-white'
      }`}>
      <Text className="font-medium text-sm">{user.name}</Text>
      <Label className="text-xs text-secondary">
        {user.phoneNumber || user.email || t(($) => $.bulkFeedbackImport.userNoContact)}
      </Label>
    </Pressable>
  )
}

export default function BulkFeedbackImportPage() {
  const { t } = useTranslations('admin')
  const { showAlert } = useAlert()
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null)
  const [searchInput, setSearchInput] = useState('')
  const [searchValue, setSearchValue] = useState('')
  const [selectedFileName, setSelectedFileName] = useState('')
  const [csvContent, setCsvContent] = useState('')
  const [importResult, setImportResult] = useState<CsvImportResult | null>(null)

  const debouncedSetSearchValue = useDebouncedCallback((nextSearchValue: string) => {
    setSearchValue(nextSearchValue)
  }, 250)

  const usersQueryInput = useMemo(
    () => ({
      page: 1,
      limit: 30,
      search: searchValue || undefined,
      role: 'realtor' as const,
    }),
    [searchValue]
  )

  const usersQuery = useQuery(trpc.admin.listUsersForAdmin.queryOptions(usersQueryInput))

  const importMutation = useMutation(
    trpc.admin.importFeedbackRequestsCsv.mutationOptions({
      onSuccess: (result) => {
        setImportResult(result)
      },
      onError: () => {
        showAlert({
          type: 'error',
          title: t(($) => $.bulkFeedbackImport.errorTitle),
          description: t(($) => $.bulkFeedbackImport.errorDescription),
        })
      },
    })
  )

  const handleSearchChange = useCallback(
    (nextSearchValue: string) => {
      setSearchInput(nextSearchValue)
      debouncedSetSearchValue(nextSearchValue)
    },
    [debouncedSetSearchValue]
  )

  const handlePickCsvFile = useCallback(async () => {
    try {
      const pickerResult = await getDocumentAsync({
        type: ['text/csv', 'text/comma-separated-values', 'application/vnd.ms-excel'],
        copyToCacheDirectory: true,
        multiple: false,
      })

      if (pickerResult.canceled) {
        return
      }

      const selectedFile = pickerResult.assets[0]
      const selectedFileContent = await readCsvContent(selectedFile.uri)

      if (!selectedFileContent.trim()) {
        showAlert({
          type: 'error',
          title: t(($) => $.bulkFeedbackImport.errorTitle),
          description: t(($) => $.bulkFeedbackImport.emptyFileError),
        })
        return
      }

      setCsvContent(selectedFileContent)
      setSelectedFileName(selectedFile.name || t(($) => $.bulkFeedbackImport.unnamedFile))
    } catch {
      showAlert({
        type: 'error',
        title: t(($) => $.bulkFeedbackImport.errorTitle),
        description: t(($) => $.bulkFeedbackImport.fileReadError),
      })
    }
  }, [showAlert, t])

  const handleRunImport = useCallback(async () => {
    if (!selectedUser || !csvContent) {
      return
    }

    await importMutation.mutateAsync({
      userId: selectedUser.userId,
      csvContent,
    })
  }, [csvContent, importMutation, selectedUser])

  const realtorUsers = usersQuery.data?.users ?? []
  const canSubmit = !!selectedUser && !!csvContent && !importMutation.isPending

  return (
    <ScreenView>
      <ScrollView>
        <ContentBox className="gap-6" variant="transparent">
          <View className="gap-3">
            <Heading2 className="text-center">{t(($) => $.bulkFeedbackImport.title)}</Heading2>
            <Body className="text-center text-secondary">
              {t(($) => $.bulkFeedbackImport.description)}
            </Body>
          </View>

          <View className="gap-3">
            <Heading3>{t(($) => $.bulkFeedbackImport.stepUserTitle)}</Heading3>
            <Input
              value={searchInput}
              onChangeText={handleSearchChange}
              placeholder={t(($) => $.bulkFeedbackImport.userSearchPlaceholder)}
              variant="box"
              className="bg-white"
            />
            {usersQuery.isLoading ? (
              <Body className="text-sm text-secondary">
                {t(($) => $.bulkFeedbackImport.loadingUsers)}
              </Body>
            ) : realtorUsers.length === 0 ? (
              <Body className="text-sm text-secondary">
                {t(($) => $.bulkFeedbackImport.noUsers)}
              </Body>
            ) : (
              <View className="gap-2">
                {realtorUsers.map((user) => (
                  <UserSelectRow
                    key={user.userId}
                    user={user}
                    isSelected={selectedUser?.userId === user.userId}
                    onSelect={setSelectedUser}
                  />
                ))}
              </View>
            )}
          </View>

          <View className="gap-3">
            <Heading3>{t(($) => $.bulkFeedbackImport.stepCsvTitle)}</Heading3>
            <Button variant="outline" onPress={() => void handlePickCsvFile()}>
              <ButtonLabel>{t(($) => $.bulkFeedbackImport.pickCsvButton)}</ButtonLabel>
            </Button>
            {!!selectedFileName && (
              <Label className="text-sm text-secondary">
                {t(($) => $.bulkFeedbackImport.selectedFile, { fileName: selectedFileName })}
              </Label>
            )}
            <Body className="text-xs text-secondary">
              {t(($) => $.bulkFeedbackImport.csvFormatHint)}
            </Body>
          </View>

          <Button disabled={!canSubmit} onPress={() => void handleRunImport()}>
            <ButtonLabel>
              {importMutation.isPending
                ? t(($) => $.bulkFeedbackImport.runningImport)
                : t(($) => $.bulkFeedbackImport.runImportButton)}
            </ButtonLabel>
          </Button>

          {!!importResult && (
            <View className="gap-4">
              <Heading3>{t(($) => $.bulkFeedbackImport.resultTitle)}</Heading3>
              <View className="gap-2 rounded-lg border border-neutral-200 bg-white p-4">
                <Label className="text-sm text-secondary">
                  {t(($) => $.bulkFeedbackImport.result.contactsCreated, {
                    count: importResult.contactsCreated,
                  })}
                </Label>
                <Label className="text-sm text-secondary">
                  {t(($) => $.bulkFeedbackImport.result.contactsSkipped, {
                    count: importResult.contactsSkipped,
                  })}
                </Label>
                <Label className="text-sm text-secondary">
                  {t(($) => $.bulkFeedbackImport.result.feedbackRequestsCreated, {
                    count: importResult.feedbackRequestsCreated,
                  })}
                </Label>
                <Label className="text-sm text-secondary">
                  {t(($) => $.bulkFeedbackImport.result.feedbackRequestsSkipped, {
                    count: importResult.feedbackRequestsSkipped,
                  })}
                </Label>
              </View>

              <View className="gap-2">
                <Heading3>{t(($) => $.bulkFeedbackImport.invalidContactsTitle)}</Heading3>
                {importResult.invalidContacts.length === 0 ? (
                  <Body className="text-sm text-secondary">
                    {t(($) => $.bulkFeedbackImport.noInvalidContacts)}
                  </Body>
                ) : (
                  <View className="gap-2">
                    {importResult.invalidContacts.map((invalidContact) => (
                      <View
                        key={`${invalidContact.rowNumber}-${invalidContact.reason}`}
                        className="gap-1 rounded-lg border border-amber-300 bg-amber-50 p-3">
                        <Text className="font-medium text-sm">
                          {t(($) => $.bulkFeedbackImport.rowLabel, {
                            rowNumber: invalidContact.rowNumber,
                          })}
                        </Text>
                        <Label className="text-xs text-secondary">
                          {getInvalidContactReasonLabel(invalidContact.reason, t)}
                        </Label>
                        <Label className="text-xs text-secondary">
                          {formatInvalidContactLine(invalidContact)}
                        </Label>
                      </View>
                    ))}
                  </View>
                )}
              </View>
            </View>
          )}
        </ContentBox>
      </ScrollView>
    </ScreenView>
  )
}
